import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCheckCircle,
  faCircleExclamation,
  faEnvelope,
  faKey,
  faQuestionCircle,
  faShieldHalved,
  faUserLock,
} from "@fortawesome/free-solid-svg-icons";

import { CognitoUserStatus } from "../../flatbuffers/generated/archive/cognito-user-status";

/**
 * How a user's live Cognito account state is coloured and explained.
 *
 * The server reads these from Cognito (one bulk ListUsers sweep, cached for
 * 30 seconds) and overlays them on the user list; they are never stored. This
 * is the sign-in account's state, separate from the LiftNet `UserStatus` next
 * to it: a user is ACTIVE in LiftNet the moment the invitation is sent, but
 * stays FORCE_CHANGE_PASSWORD here until they sign in and choose a password.
 *
 * The map is keyed by the generated enum, so adding a member to
 * `CognitoUserStatus` fails the type-check here until it is described.
 */
export interface CognitoStatusMeta {
  /** antd `Tag` colour. */
  color: string;
  icon: IconDefinition;
  /** One line an operator can act on. */
  description: string;
}

export const COGNITO_STATUS_META: Record<CognitoUserStatus, CognitoStatusMeta> = {
  [CognitoUserStatus.UNKNOWN]: {
    color: "default",
    icon: faQuestionCircle,
    description:
      "No sign-in account to report: an edge or offline user, or Cognito could not be reached for this view.",
  },
  [CognitoUserStatus.UNCONFIRMED]: {
    color: "warning",
    icon: faEnvelope,
    description: "The account exists but its email or phone has not been confirmed yet.",
  },
  [CognitoUserStatus.CONFIRMED]: {
    color: "green",
    icon: faCheckCircle,
    description: "The user has signed in and chosen a password. Nothing is pending.",
  },
  [CognitoUserStatus.ARCHIVED]: {
    color: "default",
    icon: faUserLock,
    description: "The account was archived because the user did not confirm it in time.",
  },
  [CognitoUserStatus.COMPROMISED]: {
    color: "error",
    icon: faShieldHalved,
    description: "Cognito found the user's credentials in a breach and requires a password change.",
  },
  [CognitoUserStatus.RESET_REQUIRED]: {
    color: "orange",
    icon: faKey,
    description:
      "A password reset was requested. The user must complete it before signing in again.",
  },
  [CognitoUserStatus.FORCE_CHANGE_PASSWORD]: {
    color: "gold",
    icon: faCircleExclamation,
    description:
      "Invited, but the user has not signed in yet. They must use the emailed temporary password and choose their own. If they report no email, resend the invitation.",
  },
  [CognitoUserStatus.EXTERNAL_PROVIDER]: {
    color: "blue",
    icon: faCheckCircle,
    description: "The user signs in through an external identity provider.",
  },
};

/** Every Cognito status, in wire order, derived from the generated enum. */
export const ALL_COGNITO_STATUSES: CognitoUserStatus[] = Object.values(CognitoUserStatus).filter(
  (value): value is CognitoUserStatus => typeof value === "number",
);
